import { useCallback, useEffect, useState } from "react";
import { useSignMessage, useAccount, useChainId } from "wagmi";
import { useAuthStore } from "@/store/useAuthStore";
import {
  getNonce,
  verify,
  refresh,
  logoutApi,
  AUTH_ERROR_CODES,
  type AuthResult,
} from "@/services/api/auth";

export interface UseAuthReturn {
  login: (accessCode?: string, referralCode?: string) => Promise<AuthResult>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  isLoading: boolean;
  error: AuthError | null;
  clearError: () => void;
}

export interface AuthError {
  code: string;
  message: string;
}

/**
 * Create a SIWE (Sign-In with Ethereum) message
 * Following EIP-4361 specification
 */
function createSiweMessage(
  address: string,
  nonce: string,
  chainId: number,
): string {
  const domain = window.location.host;
  const uri = window.location.origin;
  const issuedAt = new Date().toISOString();
  const expirationTime = new Date(Date.now() + 5 * 60 * 1000).toISOString(); // 5 minutes

  // EIP-4361 message format
  return `${domain} wants you to sign in with your Ethereum account:
${address}

Sign in to Helm

URI: ${uri}
Version: 1
Chain ID: ${chainId}
Nonce: ${nonce}
Issued At: ${issuedAt}
Expiration Time: ${expirationTime}`;
}

/**
 * Hook for handling SIWE authentication
 */
export function useAuth(): UseAuthReturn {
  const { address } = useAccount();
  const chainId = useChainId();
  const { signMessageAsync } = useSignMessage();
  const { setAuth, clearAuth, isSessionExpired } = useAuthStore();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Full SIWE login flow
   */
  const login = useCallback(
    async (accessCode?: string, referralCode?: string): Promise<AuthResult> => {
      if (!address) {
        throw new Error("Wallet not connected");
      }

      setIsLoading(true);
      setError(null);

      try {
        // 1. Get nonce from API
        const nonce = await getNonce();

        // 2. Create SIWE message
        const message = createSiweMessage(address, nonce, chainId);

        // 3. Sign the message
        const signature = await signMessageAsync({ message });

        // 4. Verify with API (JWT is set as HttpOnly cookie by server)
        const result = await verify(message, signature, accessCode, referralCode);

        // 5. Store auth metadata (token is in HttpOnly cookie)
        setAuth(result.expiresAt, address, result.isNewUser);

        return result;
      } catch (err) {
        const authError: AuthError = {
          code: "UNKNOWN_ERROR",
          message: "An unexpected error occurred",
        };

        if (err instanceof Error) {
          authError.message = err.message;
          authError.code =
            (err as Error & { code?: string }).code || "UNKNOWN_ERROR";

          // Handle user rejection
          if (err.message.includes("User rejected")) {
            authError.code = "USER_REJECTED";
            authError.message = "Signature request was rejected";
          }
        }

        setError(authError);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [address, chainId, signMessageAsync, setAuth],
  );

  /**
   * Logout - clear auth state and server cookie
   * Clears local state even if server logout fails to avoid stuck state
   */
  const logout = useCallback(async () => {
    try {
      // Clear server-side HttpOnly cookie
      await logoutApi();
    } catch (err) {
      // Log but continue - we still want to clear local state
      // Cookie will expire eventually or user can close browser
      console.warn("Server logout failed:", err);
    } finally {
      // Always clear client-side auth state
      clearAuth();
      setError(null);
    }
  }, [clearAuth]);

  /**
   * Refresh token if session is about to expire
   * Token is refreshed via HttpOnly cookie
   */
  const refreshToken = useCallback(async () => {
    // Guard: only refresh if there's an existing session that's about to expire
    const state = useAuthStore.getState();
    if (!state.expiresAt) {
      return;
    }

    if (!isSessionExpired()) {
      return;
    }

    setIsLoading(true);
    try {
      // Server reads token from HttpOnly cookie and sets new one
      const result = await refresh();
      if (address) {
        setAuth(result.expiresAt, address, false);
      }
    } catch (err) {
      // If refresh fails, clear auth and force re-login
      clearAuth();
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [isSessionExpired, address, setAuth, clearAuth]);

  // Auto-logout when wallet address changes
  useEffect(() => {
    if (!address) return;
    const state = useAuthStore.getState();
    if (state.walletAddress && state.walletAddress !== address.toLowerCase()) {
      // clearAuth first to prevent other instances from also calling logout
      clearAuth();
      logoutApi().catch(() => {});
    }
  }, [address, clearAuth]);

  return {
    login,
    logout,
    refreshToken,
    isLoading,
    error,
    clearError,
  };
}

export { AUTH_ERROR_CODES };
